/**
 * Order-desk notification for a paid web order.
 *
 * Portaus has been dropping the delivery address and phone number off web
 * orders since 2026-05-16 (email thread "Commande du site web"; the backend bug
 * is acknowledged and still open). Until it is fixed, a paid order is mirrored
 * to the order desk so the blanks can be filled in by hand.
 *
 * Deliberately NOT routed through error-reporter.js: that module redacts names,
 * emails and addresses behind an allowlist, which is exactly the data the desk
 * needs. This one sends the customer block verbatim, and only ever to the
 * client's own inbox via the intake API.
 */

const NOTIFY_PATH = '/orders/notify';
const SNAPSHOT_KEY = 'pendingOrderSnapshot';

/**
 * The intake API is same-origin behind Nginx. Using the path alone rather than
 * VUE_APP_PUBLIC_INTAKE_API_BASE_URL keeps this working on both www.boires.ca
 * and boires.ca — the absolute apex URL makes www visitors cross-origin, which
 * has silently broken beacons here before.
 */
function notifyUrl() {
  return `/intake-api${NOTIFY_PATH}`;
}

/**
 * Remembers what the checkout handed to /payments/intents/, so the notification
 * can report exactly that. Persisted because Stripe may bounce the customer
 * through a 3-D Secure redirect between authorize and success.
 */
export function storeOrderSnapshot(snapshot) {
  try {
    localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch (e) {
    // Private mode / quota — the caller still holds it in memory this session.
  }
}

export function readOrderSnapshot() {
  try {
    const raw = localStorage.getItem(SNAPSHOT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function clearOrderSnapshot() {
  try {
    localStorage.removeItem(SNAPSHOT_KEY);
  } catch (e) {
    // ignore
  }
}

/**
 * Best-effort mirror of a paid order to the order desk.
 *
 * Never throws and never blocks: the customer's order is already paid, and a
 * failed notification must not surface as a checkout error. The server
 * de-duplicates on paymentIntentId, so a double call is harmless.
 */
export function notifyOrderPlaced(snapshot, paymentIntentId, extra = {}) {
  if (!snapshot || !paymentIntentId) return;

  const payload = {
    paymentIntentId,
    market: snapshot.market || null,
    portausCompanyId: snapshot.portausCompanyId || null,
    isGuest: snapshot.isGuest === true,
    customer: snapshot.customer || {},
    deliveryBranch: snapshot.deliveryBranch || null,
    lines: snapshot.lines || [],
    ...extra,
  };

  try {
    const body = JSON.stringify(payload);
    if (window.fetch) {
      window.fetch(notifyUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'omit',
      }).catch(() => {});
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', notifyUrl(), true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.send(body);
  } catch (e) {
    // Silent by design — see above.
  }
}

export default {
  storeOrderSnapshot,
  readOrderSnapshot,
  clearOrderSnapshot,
  notifyOrderPlaced,
};
